/**
 * What a UI test adds to its evidence row: a line saying which commit the base
 * run was made at (and its status), then a "Screenshots" block showing
 * before, after and difference images per checkpoint, drawn with the Visual page's image pane.
 *
 * @module features/proofs/components/UiTestCheckpoints
 */
import React from 'react';
import { StatusBadge, Text, normalizeRunStatus } from '@chatsdet/ui';
import { Pane } from '../../visual/VisualPane';
import type { ProofCheckpoint, ProofEvidence } from '../types';
import { sha7 } from '../evidenceKinds';

type Ui = NonNullable<Extract<ProofEvidence, { kind: 'e2e' }>['ui']>;

const FullSize: React.FC<{ url: string | null; label: string; children: React.ReactNode }> = ({ url, label, children }) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" aria-label={label} className="block min-w-0">
      {children}
    </a>
  ) : (
    <>{children}</>
  );

const fullSizeLabel = (checkpoint: ProofCheckpoint, title: string) =>
  `${checkpoint.label}: open ${title} full size (new tab)`;

const CheckpointImages: React.FC<{ checkpoint: ProofCheckpoint; hasBase: boolean }> = ({ checkpoint, hasBase }) => {
  const { status, imageUrl, baselineUrl, diffUrl } = checkpoint;
  if (status === 'pending') return <Text color="muted">Still capturing the earlier version.</Text>;
  if (status === 'unavailable') return <Text color="muted">No comparison is available for this checkpoint.</Text>;
  if (status === 'no_base') {
    return (
      <div className="max-w-md">
        <FullSize url={imageUrl} label={fullSizeLabel(checkpoint, 'After')}>
          <Pane title="After" url={imageUrl} missing="No screenshot" />
        </FullSize>
        {hasBase && <Text color="muted">The earlier version has no screenshot here.</Text>}
      </div>
    );
  }
  const showDiff = status === 'changed';
  return (
    <div className={`grid grid-cols-1 gap-4 ${showDiff ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
      <FullSize url={baselineUrl} label={fullSizeLabel(checkpoint, 'Before')}>
        <Pane title="Before" url={baselineUrl} missing="No earlier screenshot" />
      </FullSize>
      <FullSize url={imageUrl} label={fullSizeLabel(checkpoint, 'After')}>
        <Pane title="After" url={imageUrl} missing="No screenshot" />
      </FullSize>
      {showDiff && (
        <FullSize url={diffUrl} label={fullSizeLabel(checkpoint, 'Difference')}>
          <Pane title="Difference" url={diffUrl} missing="No difference image" />
        </FullSize>
      )}
    </div>
  );
};

const STATUS_TEXT: Record<ProofCheckpoint['status'], string> = {
  changed: 'Changed',
  unchanged: 'Unchanged',
  incompatible: 'Different size',
  unavailable: 'Not available',
  no_base: 'No earlier version',
  pending: 'In progress',
};

export const UiTestCheckpoints: React.FC<{ ui: Ui }> = ({ ui }) => (
  <div className="space-y-3">
    {ui.baseError ? (
      <Text color="muted">{`The earlier version could not be checked: ${ui.baseError}`}</Text>
    ) : ui.baseSha ? (
      <div className="flex items-center gap-2">
        <Text color="muted">{`Compared with the earlier version ${sha7(ui.baseSha)}`}</Text>
        {ui.baseRun && (
          <>
            <span className="sr-only">{`earlier version run ${ui.baseRun.status}`}</span>
            <span aria-hidden>
              <StatusBadge status={normalizeRunStatus(ui.baseRun.status)} size="sm" />
            </span>
          </>
        )}
      </div>
    ) : null}
    {ui.checkpoints.length > 0 && (
      <section className="space-y-3" aria-label="Screenshots">
        <Text weight="semibold">Screenshots</Text>
        {ui.checkpoints.map((checkpoint, index) => (
          <div key={`${checkpoint.nodeId}:${index}`} className="space-y-2">
            <Text size="sm" weight="medium">{`${checkpoint.label} · ${STATUS_TEXT[checkpoint.status]}`}</Text>
            <CheckpointImages checkpoint={checkpoint} hasBase={ui.baseSha !== null} />
          </div>
        ))}
      </section>
    )}
  </div>
);
